export default `---
slug: morocco
country: Morocco
countryCode: MAR
continent: Africa
year: 2023
title: "Lass and the Sand Dunes"
description: "Marrakesh to the Sahara to Fez. One week, four friends, fourteen hours through the Atlas Mountains, and a conversation I still think about."
thumbnail: /images/travels/MAR/dunes.jpg
relatedLinks: []
---

May 2023. Me, Silvia, Edoardo, and Alberto landed in Marrakesh and picked up a rental car. The plan: drive south through the Atlas Mountains to Merzouga and the edge of the Sahara, then north to Fez, then home.

It was my first holiday as an actual working adult, and I was going to make it count.

## Marrakesh

The medina is an assault on the senses in the best possible way — the spice bazaars, the narrow alleys, the sound of a dozen different conversations layered on top of each other, the smell of street food and something you can't quite identify. The souks are the obvious draw, though if you want to avoid the full-on tourist trap experience (which I do), you navigate them with a degree of caution.

![Topi and the boys in the Yves Saint Laurent garden, Marrakesh](/images/travels/MAR/marrakesh_garden.jpg "Topi and the boys in the Yves Saint Laurent garden in Marrakesh")

Silvia had a harder time with some of it. The stray dogs, the way certain animals were treated — donkeys in particular — made for a heavy few hours. Moroccan food is mainly lamb tagine and variations thereof, which is <i>very</i> good if you eat meat, and considerably less exciting if you're not much of a meat person. Silvia is not much of a meat person. But I loved it.

## Through the Atlas

The best part of Morocco wasn't any single city. <b>It was the driving.</b>

The route from Marrakesh to Merzouga takes you straight through the High Atlas Mountains, and the variety of what you see — in a single day, in a single country — is genuinely spectacular. Marrakesh is dry and sandy and warm browns in every direction. Then you climb. The mountains appear, then snow on the peaks. Then you descend through green valleys thick with palm trees, with rivers running through the rock. We stopped several times just to look.

![Through the Atlas Mountains](/images/travels/MAR/morocco_2.jpg)

The final stretch into Merzouga was at night, which was less scenic and more terrifying — winding roads, no visibility, everyone in the car staring at Google Maps and willing the ETA to stop moving backwards. We arrived very late, very tired, and very hungry.

## Lass

The guesthouse owner had left the lights on and a full table of food ready for us.

His name was Lass. He sat with us while we ate, and when we finally came up for air, he poured tea and told us his story.

<b>Lass was born a Berber nomad.</b> A shepherd, never formally educated, one of seven brothers and sisters sharing a single room. He is now the owner of the guesthouse where we sat, speaks six languages (Italian included, which meant we spent most of the evening in Italian), and is the President of the Association for the Preservation of the Sand Dunes of the Sahara — a position that had, at some point, required a direct conversation with the Moroccan royal family.

From nomad shepherd to guardian of the desert. From one room to a guesthouse. All of it self-built, step by step, over decades.

I travel because of conversations like that one. If someone asked me to choose a single memory from the whole of Morocco, it wouldn't be the desert, as extraordinary as that was — <i>it would be that terrace, that tea, and that man.</i>

## The Sahara

The next morning we rode camels into the dunes at sunrise. I'll be honest: the camels are fine but not comfortable, and after about thirty minutes the romance of it wears off and you're mainly trying to keep your balance.

But the dunes themselves — the colour of them, the orange-brown, the strange sound the sand makes when the wind moves it — were unlike anything I'd seen.

![Sunset on the dunes, Merzouga](/images/travels/MAR/dunes.jpg)

We spent the night under the open sky, and yes, I had perhaps slightly inflated expectations for the stars based on photographs. Cameras see better than the naked eye, it turns out. But the stars were still extraordinary, bright and sharp in a way they never are back home.

## Fez, Alberto, and the Blue City That Wasn't

The drive to Fez the next day was beautiful again — palm valleys, desert canyons, the country revealing itself in layers. Alberto spent a significant portion of it in acute digestive distress from some food he'd eaten, and at some point we pulled into a restaurant in the absolute middle of nowhere, walked in looking like the strangest apparition the owner had ever seen, and Alberto went directly to the bathroom without making eye contact with anyone. We still laugh about this.

Fez was worth the drive — the ancient medina, the narrow alleys, the leather tanneries, the old covered market. Genuinely unlike anywhere else.

The last day, we'd planned to visit Chefchaouen, the famous blue city in the Rif Mountains. <b>I got sick. Edoardo got sick.</b> We stayed in bed while Silvia and Alberto went without us. They reported back that it was nice, but perhaps not worth getting sick over. I chose to believe them.

Morocco was exactly what I'd always wanted it to be — chaotic, beautiful, occasionally difficult, and full of things I didn't expect. First trip as a working adult. Loved every minute of it.
`;
